import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { Heading } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import { ContentfulInstructorLedTraining } from 'src/graphql/queries/GetInstructorLedTraining';

interface ContentProps {
  iltData?: ContentfulInstructorLedTraining;
}

export const Content = ({ iltData }: ContentProps) => {
  const hasDescription = iltData?.description?.json;

  if (!hasDescription) {
    return null;
  }

  return (
    <div css={descriptionSectionStyles}>
      <Heading level={3} color={token('color.text')}>
        Class description
      </Heading>
      <div css={descriptionContentStyles}>
        <div css={richTextContentStyles}>{documentToReactComponents(iltData.description.json)}</div>
      </div>
    </div>
  );
};

const descriptionSectionStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.400'),
});

const descriptionContentStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
});

const richTextContentStyles = css({
  color: token('color.text'),
  fontFamily: 'Inter, sans-serif',
  fontSize: '1.25rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '2rem',

  [media.below.md]: {
    fontSize: '1rem',
    lineHeight: '1.5rem',
  },

  '& p': {
    margin: 0,
    marginBottom: '1.5rem',

    '&:last-child': {
      marginBottom: 0,
    },
  },

  '& b, & strong': {
    fontWeight: 700,
  },

  '& h4': {
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: 'Charlie Display, sans-serif',
    fontSize: '1.5rem',
    fontWeight: 600,
    margin: 0,
    marginBottom: '1.5rem',

    [media.below.md]: {
      fontSize: '1.25rem',
    },

    '&:last-child': {
      marginBottom: 0,
    },
  },

  '& h4 b, & h4 strong': {
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
  },

  '& a': {
    color: token('color.link'),
    textDecoration: 'underline',

    '&:hover': {
      color: token('color.link.pressed'),
    },
  },

  '& ul, & ol': {
    margin: 0,
    marginBottom: '1.5rem',
    paddingLeft: token('space.400'),

    '&:last-child': {
      marginBottom: 0,
    },
  },

  '& li': {
    marginBottom: token('space.100'),

    '&:last-child': {
      marginBottom: 0,
    },
  },
});
